/**
 * Guest stays: a stay ties a room, dates, one invite per guest and (Woodlands) the HA guest-stay
 * helpers together. REST shapes for /api/admin/homes/:home/stays* (owner view) and
 * /api/homes/:home/stay (the guest's own stay). The server builds every membership from a stay;
 * clients never send grants.
 */
import { z } from 'zod';
import { InviteStatus } from './access.js';
/** "HH:MM", 24 h. */
export const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
/** guest = a room (+ shared rooms); sitter = the whole home (house-sit). */
export const StayKind = z.enum(['guest', 'sitter']);
/** What the guests get during the stay (before arrival everything is read-only). */
export const StayAccess = z.object({
    /** Front door (Woodlands latch; flat door + street intercom). */
    door: z.boolean().default(true),
    /** The whole-house alarm panel. */
    alarm: z.enum(['none', 'arm_disarm']).default('arm_disarm'),
    wifi: z.boolean().default(true),
    info: z.boolean().default(true),
    music: z.boolean().default(true),
    /** Guests may set their room's wake / bed light times (rooms with light-time helpers only). */
    lightTimes: z.boolean().default(true),
    /** Extra rooms they may use (kitchen, living…). */
    sharedRooms: z.array(z.string().min(1).max(64)).max(40).default([]),
});
/** Lead time default (owner decision 2026-10-08): helpers on 12 h before arrival so the room is warm. */
export const STAY_LEAD_HOURS = 12;
/** Woodlands: "Run the house for guests" (input_boolean.guest_stay* + its until helper). */
export const StayHelpers = z.object({
    apply: z.boolean().default(true),
    leadHours: z.number().int().min(0).max(72).default(STAY_LEAD_HOURS),
});
export const StayGuestInput = z.object({ name: z.string().trim().min(1).max(60) });
export const MAX_STAY_GUESTS = 6;
export const CreateStayInput = z.object({
    /** null = whole home (house-sit). */
    roomId: z.string().min(1).max(64).nullable(),
    kind: StayKind.default('guest'),
    /** Defaults to the guests' names ("Anna & Tom"). */
    title: z.string().trim().max(80).optional(),
    guests: z.array(StayGuestInput).min(1).max(MAX_STAY_GUESTS),
    /** Epoch seconds. */
    arrivesAt: z.number().int().positive(),
    departsAt: z.number().int().positive(),
    welcome: z.string().max(1000).default(''),
    access: StayAccess.prefault({}),
    helpers: StayHelpers.prefault({}),
});
export const UpdateStayInput = z.object({
    roomId: z.string().min(1).max(64).nullable().optional(),
    title: z.string().trim().min(1).max(80).optional(),
    arrivesAt: z.number().int().positive().optional(),
    departsAt: z.number().int().positive().optional(),
    welcome: z.string().max(1000).optional(),
    access: StayAccess.optional(),
    helpers: StayHelpers.optional(),
});
export const StayStatus = z.enum(['upcoming', 'active', 'ended']);
export const StayGuest = z.object({
    inviteId: z.string(),
    name: z.string(),
    code: z.string(),
    status: InviteStatus,
    sessionCount: z.number(),
    lastSeenAt: z.number().nullable(),
    guestUserId: z.string().nullable(),
    /** First redeem (epoch s), null = not opened yet. */
    openedAt: z.number().nullable(),
    /** First door release in the stay window. */
    arrivedAt: z.number().nullable(),
});
export const Stay = z.object({
    id: z.string(),
    homeId: z.string(),
    roomId: z.string().nullable(),
    roomName: z.string(),
    kind: StayKind,
    title: z.string(),
    arrivesAt: z.number(),
    departsAt: z.number(),
    welcome: z.string(),
    access: StayAccess,
    helpers: StayHelpers,
    endedAt: z.number().nullable(),
    createdBy: z.string(),
    createdAt: z.number(),
    status: StayStatus,
    guests: z.array(StayGuest),
});
/** A guest room's HA helper pair as the app sees it (Woodlands). */
export const StayHelperView = z.object({
    stayEntity: z.string(),
    untilEntity: z.string(),
    /** Live HA state of the boolean. */
    state: z.enum(['on', 'off', 'unknown']),
    /** Live HA value of the until helper ("2026-10-12 11:00:00"). */
    until: z.string().nullable(),
    /** The app turned it on and will turn it off. */
    appliedByApp: z.boolean(),
    appliedUntil: z.string().nullable(),
    /** The app applied it but HA says off before departure (someone toggled it). */
    drift: z.boolean(),
});
export const GuestRoomCard = z.object({
    /** null = whole home. */
    roomId: z.string().nullable(),
    name: z.string(),
    helpers: StayHelperView.nullable(),
    current: z.object({ stayId: z.string(), title: z.string(), departsAt: z.number() }).nullable(),
    next: z.object({ stayId: z.string(), title: z.string(), arrivesAt: z.number() }).nullable(),
});
export const StayNotifyPrefs = z.object({
    redeemed: z.boolean().default(true),
    arrived: z.boolean().default(true),
    door: z.boolean().default(false),
    alarm: z.boolean().default(true),
    denied: z.boolean().default(true),
    digest: z.boolean().default(true),
    ended: z.boolean().default(false),
    helperFailed: z.boolean().default(true),
});
export const StaySettings = z.object({ notify: StayNotifyPrefs.prefault({}) });
export const StaysResponse = z.object({
    stays: z.array(Stay),
    rooms: z.array(GuestRoomCard),
    /** Candidate shared rooms (id, name) and the defaults the Start-a-stay sheet fills in. */
    sharedRoomOptions: z.array(z.object({ id: z.string(), name: z.string(), defaultOn: z.boolean() })),
    defaults: z.object({ checkInTime: HHMM, checkOutTime: HHMM, leadHours: z.number(), hasHelpers: z.boolean(), hasDoor: z.boolean(), hasAlarm: z.boolean() }),
});
export const StayDetailResponse = z.object({
    stay: Stay,
    activity: z.array(z.object({ at: z.number(), who: z.string().nullable(), kind: z.string(), detail: z.string().nullable(), allowed: z.boolean() })),
    helpers: StayHelperView.nullable(),
});
// ---------- the guest's own stay ----------
export const GuestStayPhase = z.enum(['pre', 'during', 'ended']);
export const GuestLightTimes = z.object({
    wake: HHMM,
    bed: HHMM,
    /** The guest's times are in force (else the room's usual ones). */
    overriding: z.boolean(),
    /** In window with the light-times access. */
    editable: z.boolean(),
});
export const LightTimesInput = z.object({ wake: HHMM, bed: HHMM });
/** GET /api/homes/:home/stay (guest users only). */
export const GuestStay = z.object({
    stayId: z.string(),
    homeId: z.string(),
    homeName: z.string(),
    guestName: z.string(),
    title: z.string(),
    kind: StayKind,
    roomId: z.string().nullable(),
    roomName: z.string().nullable(),
    arrivesAt: z.number(),
    departsAt: z.number(),
    welcome: z.string(),
    checkInTime: HHMM,
    checkOutTime: HHMM,
    address: z.string(),
    arrivalNotes: z.string(),
    phase: GuestStayPhase,
    access: StayAccess,
    sharedRooms: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Door entities this stay grants (empty when door access is off). */
    door: z.array(z.string()),
    /** Alarm panel this stay grants (null when off). */
    alarm: z.string().nullable(),
    lightTimes: GuestLightTimes.nullable(),
});
// ---------- pure helpers ----------
export function stayPhase(s, at) {
    if (s.endedAt != null && s.endedAt <= at)
        return 'ended';
    if (at >= s.departsAt)
        return 'ended';
    return at < s.arrivesAt ? 'pre' : 'during';
}
export function stayStatus(s, at) {
    const p = stayPhase(s, at);
    return p === 'pre' ? 'upcoming' : p === 'during' ? 'active' : 'ended';
}
/** "Anna", "Anna & Tom", "Anna, Tom & Sam". */
export function stayTitle(names) {
    const n = names.map((x) => x.trim()).filter(Boolean);
    if (n.length <= 1)
        return n[0] ?? 'Guests';
    return `${n.slice(0, -1).join(', ')} & ${n[n.length - 1]}`;
}
export const DEFAULT_CHECK_IN = '15:00';
export const DEFAULT_CHECK_OUT = '11:00';
/** Placeholders: {name} {home} {dates} {arrive} {depart} {checkIn} {checkOut} {link} {code}. */
export const DEFAULT_STAY_TEMPLATES = {
    sms: 'Hi {name}! Here is your access for {home}, {dates}. Open {link} (or enter code {code} at sign-in). Door, alarm and Wi-Fi unlock at check-in, {arrive}.',
    email: 'Hi {name},\n\nLooking forward to having you at {home}, {dates}.\n\nYour guest app: {link}\nIf the link asks for a code, enter {code}.\n\n' +
        'You can open it now to see the house info. The door, alarm and Wi-Fi unlock at check-in ({arrive}). Check-out is {depart}.\n\nSee you soon!',
};
export function fillStayTemplate(tpl, vars) {
    return tpl.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
}
//# sourceMappingURL=stays.js.map